import { api, RouterInput, RouterOutput } from "../../services/rpc/RpcProvider";
import { useToast } from "@introist/react-foundation/v2";
import { useCallback } from "react";

export type Form = RouterOutput["forms"]["v2"]["find"];
export type FormCreate = RouterInput["forms"]["v2"]["create"];
export type FormUpdate = RouterInput["forms"]["v2"]["update"];

export type FormShareRequest = RouterInput["forms"]["v2"]["share"];
export type FormShareResponse = RouterOutput["forms"]["v2"]["share"];

export type FormPreview = RouterOutput["forms"]["v2"]["preview"];

export const useForms = () => {
  const { data: forms, isLoading } = api.forms.v2.list.useQuery({});

  return { forms, loading: isLoading };
};

export const useForm = (id: string) => {
  const { data: form } = api.forms.v2.find.useQuery({ id });
  return { form };
};

export const usePreviewForm = (id: string) => {
  const { data: form } = api.forms.v2.preview.useQuery({ formId: id });
  return { form };
};

export const useFormActions = () => {
  const toast = useToast();
  const apiUtils = api.useContext();

  const listOp = apiUtils.forms.v2.list;
  const findOp = apiUtils.forms.v2.find;
  const previewOp = apiUtils.forms.v2.preview;

  const createOp = api.forms.v2.create.useMutation();
  const updateOp = api.forms.v2.update.useMutation();
  const archiveOp = api.forms.v2.archive.useMutation();
  const shareOp = api.forms.v2.share.useMutation();
  const duplicateOp = api.forms.v2.duplicate.useMutation();

  const create = useCallback(
    async (req: FormCreate) => {
      return createOp
        .mutateAsync(req)
        .then(async created => {
          await listOp.refetch();
          return created;
        })
        .catch(e => {
          toast.error("Failed to create Form");
          throw e;
        });
    },
    [createOp, listOp, toast]
  );

  const update = useCallback(
    async (req: FormUpdate) => {
      return updateOp
        .mutateAsync(req)
        .then(async updated => {
          await listOp.refetch();
          await findOp.refetch({ id: req.id });
          await previewOp.refetch({ formId: req.id });
          return updated;
        })
        .catch(e => {
          toast.error("Failed to create Form");
          throw e;
        });
    },
    [updateOp, listOp, findOp, previewOp, toast]
  );

  const archive = useCallback(
    async (id: string) => {
      await archiveOp
        .mutateAsync({ id })
        .then(async () => {
          await listOp.refetch();
          toast.success("Form archived");
        })
        .catch(() => {
          toast.error("Failed to archive Form");
        });
    },
    [archiveOp, toast, listOp]
  );

  const share = useCallback(
    async (req: FormShareRequest) => {
      return shareOp.mutateAsync(req).catch(e => {
        toast.error("Failed to share Form");
        throw e;
      });
    },
    [shareOp, toast]
  );

  const duplicate = useCallback(
    async (id: string) => {
      return duplicateOp
        .mutateAsync({ id })
        .then(async created => {
          await listOp.refetch();
          toast.success("Form duplicated");
          return created;
        })
        .catch(e => {
          toast.error("Failed to duplicate Form");
          throw e;
        });
    },
    [duplicateOp, listOp, toast]
  );

  return { create, update, archive, share, duplicate };
};
